// Takvim DTO tipleri — DAL karşılıkları birebir yansıtılır (ASP.NET Core varsayılan camelCase serileştirmesi):
//   Octo.Models/System/SystemCalendarSource.cs          → CalendarSource
//   Octo.Models/System/CalendarSourceType.cs            → CALENDAR_SOURCE_TYPE
//   Octo.Models/Dtos/HomeCalendarEvent.cs               → MergedCalendarEvent  (GET api/calendar/events/{userId})
//   Octo.Dal/Services/CalendarMergeService.cs           → CalendarSourceLabel
//   Octo.Models/System/SystemCalendarEvent.cs           → LocalCalendarEvent   (api/system/CalendarEvents*)
//   Octo.Dal/Services/ICalSyncService.cs → SyncResult   → CalendarSyncResult
// Blazor karşılığı: Octo.Web.Server/Components/Widgets/CalendarWidget.razor (DxScheduler + DxSchedulerDataStorage).

/** CalendarSourceType enum'u (int olarak taşınır). */
export const CALENDAR_SOURCE_TYPE = {
  Local: 0,
  DataSourceObject: 1,
  ICalUrl: 2,
  Google: 3,
  Outlook: 4,
} as const;

export type CalendarSourceTypeId = (typeof CALENDAR_SOURCE_TYPE)[keyof typeof CALENDAR_SOURCE_TYPE];

/** Tb_Rn_Sys_CalendarSources satırı. */
export interface CalendarSource {
  calendarSourceId: string;
  userId: string;
  sourceName: string | null;
  sourceType: CalendarSourceTypeId;
  color: string | null;
  isEnabled: boolean;
  isReadOnly: boolean;
  sortOrder: number;
  /** Kaynak tipine özel JSON yapılandırma (Local "{}", ICalUrl {"url":…}, DataSourceObject {…}). */
  sourceConfig: string | null;
  externalCalendarId?: string | null;
  lastSyncUtc?: string | null;
  syncIntervalMinutes?: number;
  syncErrorMessage?: string | null;
  addDate?: string;
  addUserId?: string;
  updateDate?: string | null;
  updateUserId?: string | null;
}

/** Yeni kaynak oluşturma gövdesi (POST api/calendar/sources). */
export interface CalendarSourceCreate {
  userId: string;
  sourceName: string;
  sourceType: CalendarSourceTypeId;
  color: string;
  isEnabled: boolean;
  isReadOnly: boolean;
  sourceConfig: string;
  addUserId: string;
  syncIntervalMinutes?: number;
}

/** DataSourceObject kaynağının SourceConfig JSON'u (Octo.Models/Dtos/DataSourceObjectConfig.cs). */
export interface DataSourceObjectConfig {
  objectId?: string | null;
  dateField?: string | null;
  endDateField?: string | null;
  titleField?: string | null;
  descriptionField?: string | null;
  filter?: string | null;
}

/** iCal aboneliğinin SourceConfig JSON'u. */
export interface ICalUrlConfig {
  url?: string | null;
}

/** CalendarMergeService'in ürettiği birleşik etkinlik (HomeCalendarEvent). */
export interface MergedCalendarEvent {
  eventId: string;
  calendarSourceId: string;
  sourceName: string | null;
  sourceColor: string | null;
  isReadOnly: boolean;
  subject: string | null;
  description: string | null;
  /** Sunucu yerel saati, dilim eki yok: "2026-04-14T10:00:00". */
  start: string;
  end: string;
  allDay: boolean;
  labelId: number;
  statusId: number;
  recurrenceInfo: string | null;
  location: string | null;
}

/** DxScheduler etiket kaynağı — kaynak adı + rengi. */
export interface CalendarSourceLabel {
  id: number;
  caption: string | null;
  color: string | null;
}

/** GET api/calendar/events/{userId} yanıtı. */
export interface MergedEventsResponse {
  events: MergedCalendarEvent[] | null;
  labels: CalendarSourceLabel[] | null;
}

/** POST api/calendar/sources/{sourceId}/sync yanıtı. */
export interface CalendarSyncResult {
  success: boolean;
  error?: string | null;
  newEvents?: number;
  updatedEvents?: number;
  deletedEvents?: number;
}

/** Tb_Rn_Sys_CalendarEvents satırı — yalnızca Local kaynaklarda yazılabilir. */
export interface LocalCalendarEvent {
  calendarEventId: string;
  userId: string;
  subject: string | null;
  description: string | null;
  start: string;
  end: string;
  allDay: boolean;
  labelId: number | null;
  statusId: number | null;
  recurrenceInfo: string | null;
  location: string | null;
  calendarSourceId: string | null;
  addUserId?: string;
  updateUserId?: string | null;
}

/** Yerel etkinlik yazma gövdesi (create/update ortak). */
export interface LocalCalendarEventInput {
  calendarEventId?: string;
  userId: string;
  subject: string;
  description: string;
  start: string;
  end: string;
  allDay: boolean;
  location: string;
  labelId: number;
  statusId: number;
  recurrenceInfo: string | null;
  calendarSourceId: string | null;
  addUserId?: string;
  updateUserId?: string | null;
}

/** Görünümlerin kullandığı normalize edilmiş etkinlik (tarihler Date, renk çözülmüş). */
export interface CalendarEvent {
  /** Birleşik uçtan gelen EventId — yerel etkinliklerde CalendarEventId (GUID). */
  id: string;
  sourceId: string;
  sourceName: string;
  color: string;
  title: string;
  description: string;
  location: string;
  start: Date;
  end: Date;
  allDay: boolean;
  /** Kaynak salt okunur ya da yerel değilse düzenlenemez. */
  readOnly: boolean;
  /** Local kaynağa ait, api/system/CalendarEvents ile düzenlenebilir mi. */
  isLocal: boolean;
  labelId: number;
  statusId: number;
}

export type CalendarViewKind = 'month' | 'week' | 'day' | 'agenda';

export interface DateRange {
  start: Date;
  /** Üst sınır dışlayıcıdır (yarı açık aralık). */
  end: Date;
}
